/* Thẻ kết quả chấm Reflection: AI (Simba) và Mentor, kèm thang mức rubric 0–4. */
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Icon } from "@/components/icon"
import { cn } from "@/lib/utils"
import { LEVEL_LABELS, score100 } from "./shared"

/** Thang mức 0..max; mức đang đạt được tô cam. Mobile: cuộn ngang trong khung. */
export function RubricLevels({ selected, max = 4, label }: { selected: number; max?: number; label: string }) {
  return (
    <ol aria-label={`${label}: ${selected} trên ${max}`} className="grid grid-cols-5 gap-1.5">
      {Array.from({ length: max + 1 }, (_, level) => {
        const on = level === selected
        return (
          <li
            key={level}
            aria-current={on ? "true" : undefined}
            className={cn(
              "flex min-w-0 flex-col items-center gap-0.5 rounded-xl border-[1.5px] px-1 py-2 text-center",
              on ? "border-orange bg-orange text-white" : level < selected ? "border-transparent bg-soft text-accent-foreground" : "border-line text-muted-foreground",
            )}
          >
            <span className="font-heading text-base font-bold">{level}</span>
            <span className="hidden text-sm leading-tight sm:block">{LEVEL_LABELS[level] || `Level ${level}`}</span>
          </li>
        )
      })}
    </ol>
  )
}

function ScoreBubble({ score, tone }: { score: number; tone: "orange" | "success" | "ink" }) {
  return (
    <span
      className={cn(
        "grid size-18 shrink-0 place-items-center rounded-full font-heading",
        tone === "success" && "bg-success-soft text-success",
        tone === "orange" && "bg-soft text-orange-strong",
        tone === "ink" && "bg-ink text-white",
      )}
    >
      <span className="text-center leading-none">
        <span className="block text-2xl font-bold">{score}</span>
        <span className="text-sm opacity-80">/100</span>
      </span>
    </span>
  )
}

function Criterion({ c, max }: { c: any; max?: number }) {
  const s = Number(c.score)
  const weak = s < 4
  return (
    <section className={cn("flex flex-col gap-3 rounded-2xl border-[1.5px] p-4", weak ? "border-line bg-paper" : "border-success/30 bg-success-soft/40")}>
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 font-heading text-base font-semibold text-ink">
          <Icon name={weak ? "target" : "check-circle"} className={cn("size-5", weak ? "text-orange" : "text-success")} />
          {c.label}
        </h4>
        <Badge variant={weak ? "warning" : "success"}>
          {s}/{max ?? 4} · {LEVEL_LABELS[s] || `Level ${s}`}
        </Badge>
      </header>
      <RubricLevels selected={s} max={max} label={c.label} />
      {c.feedback && <p className="text-sm text-ink">{c.feedback}</p>}
      {c.evidence && (
        <blockquote className="rounded-xl border-l-4 border-gold bg-sand px-3 py-2 text-sm text-muted-foreground">
          <b className="block text-ink">Bằng chứng</b>
          {c.evidence}
        </blockquote>
      )}
    </section>
  )
}

export function AiEvaluationCard({ item, onImprove, compact = false }: { item: any; onImprove: () => void; compact?: boolean }) {
  const score = score100(item.normalized_score)
  const completed = item.completion_status === "completed" || score > 90
  const criteria: any[] = Array.isArray(item.criteria) ? item.criteria : []
  const weak = criteria.filter((c) => Number(c.score) < 4)
  const questions: string[] = Array.isArray(item.improvement_questions) ? item.improvement_questions.slice(0, 3) : []
  const flags: string[] = Array.isArray(item.integrity_flags) ? item.integrity_flags : []
  return (
    <Card size={compact ? "sm" : "default"}>
      <CardContent className="flex flex-col gap-5">
        <header className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-orange">
              <Icon name="bot" className="size-4" /> Simba đánh giá · Lần {item.revision || 1}
            </p>
            <h3 className="font-heading text-xl font-bold text-ink">{completed ? "Con đã hoàn thành!" : "Bài này còn có thể tốt hơn"}</h3>
            {item.overall_feedback && <p className="text-sm text-muted-foreground">{item.overall_feedback}</p>}
          </div>
          <ScoreBubble score={score} tone={completed ? "success" : "orange"} />
        </header>
        {flags.length > 0 && (
          <aside className="flex flex-col gap-1 rounded-2xl bg-danger-soft px-4 py-3 text-sm text-danger">
            <b>Điều cần sửa trước tiên</b>
            {flags.map((f) => (
              <span key={f}>{f}</span>
            ))}
          </aside>
        )}
        <div className="grid gap-3 lg:grid-cols-2">
          {criteria.map((c) => (
            <Criterion key={c.criterion_key || c.label} c={c} />
          ))}
        </div>
        {!completed && (
          <section className="flex flex-col gap-3 rounded-2xl bg-soft p-4 sm:p-5">
            <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">F cần nâng cấp</p>
            <h4 className="font-heading text-lg font-bold text-ink">
              {weak.map((c) => String(c.label).replace(/\s.+$/, "")).join(" · ") || "Bài Reflection"}
            </h4>
            <p className="text-sm text-ink">Trả lời 3 câu này rồi sửa lại bài của con:</p>
            <ol className="flex flex-col gap-2">
              {questions.map((q, i) => (
                <li key={q} className="flex gap-3 rounded-xl bg-card px-3 py-2.5 text-sm text-ink">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-orange font-bold text-white">{i + 1}</span>
                  {q}
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={onImprove}>
                <Icon name="sparkles" className="size-4" /> Nâng cấp bài
              </Button>
              <span className="text-sm text-muted-foreground">Cần trên 90 điểm để hoàn thành.</span>
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  )
}

export function MentorEvaluationCard({ evaluation, compact = false }: { evaluation: any; compact?: boolean }) {
  const score = score100(evaluation?.normalized_score)
  const criteria: any[] = Array.isArray(evaluation?.criteria) ? evaluation.criteria : []
  return (
    <Card size={compact ? "sm" : "default"} className="border-gold/60">
      <CardContent className="flex flex-col gap-5">
        <header className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-orange">
              <Icon name="graduation" className="size-4" /> Nhận xét từ mentor
            </p>
            <h3 className="font-heading text-xl font-bold text-ink">Mentor đã chấm lại reflection này</h3>
            {evaluation?.overall_feedback && <p className="text-sm text-muted-foreground">{evaluation.overall_feedback}</p>}
          </div>
          <ScoreBubble score={score} tone="ink" />
        </header>
        <div className="grid gap-3 lg:grid-cols-2">
          {criteria.map((c) => (
            <Criterion key={c.criterion_key || c.label} c={c} max={Math.max(4, Number(c.max_score || 4))} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
